/**
 * FeedbackFAB — Floating Action Button for the feedback widget.
 *
 * Three visual states:
 *  - idle (no session): grey microphone-feedback icon
 *  - active (session in progress): green with badge showing comment count
 *  - recording: pulsing red ring
 *
 * The FAB persists across all pages and is positioned at bottom-right
 * of the viewport (fixed positioning via CSS).
 */

import { cn } from "../../utils/cn";

interface FeedbackFABProps {
  readonly hasSession: boolean;
  readonly isRecording: boolean;
  readonly commentCount: number;
  readonly onClick: () => void;
}

export function FeedbackFAB({
  hasSession,
  isRecording,
  commentCount,
  onClick,
}: FeedbackFABProps) {
  let stateClass = "fb-fab--idle";
  if (isRecording) {
    stateClass = "fb-fab--recording";
  } else if (hasSession) {
    stateClass = "fb-fab--active";
  }

  const label = hasSession ? "Open feedback panel" : "Start feedback session";

  return (
    <button
      type="button"
      className={cn("fb-fab", stateClass)}
      onClick={onClick}
      aria-label={label}
      title={label}
    >
      {/* Microphone with speech lines icon */}
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {/* Microphone body */}
        <rect
          x="9"
          y="2"
          width="6"
          height="11"
          rx="3"
          fill="currentColor"
          stroke="none"
        />
        {/* Microphone arc */}
        <path d="M5 10a7 7 0 0 0 14 0" />
        {/* Stand */}
        <line x1="12" y1="17" x2="12" y2="20" />
        <line x1="9" y1="20" x2="15" y2="20" />
        {/* Speech/feedback lines */}
        <line x1="19" y1="4" x2="22" y2="4" />
        <line x1="19" y1="7" x2="21" y2="7" />
      </svg>
      <span className="fb-fab-label" aria-hidden="true">
        {isRecording ? "Recording\u2026" : "Feedback"}
      </span>
      {/* Badge */}
      {hasSession && commentCount > 0 && !isRecording && (
        <span className="fb-fab-badge">{commentCount}</span>
      )}
    </button>
  );
}
